import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  initializeFirebase,
  getFirebaseAuth,
  AuthProvider,
  AppSubscriptionProvider,
  Header,
  useBackendAuth,
  Toaster,
  AppAccessGuard,
  BetaAccessDeniedPage,
  useAuth,
} from '@aaas/shared';
import { onAuthStateChanged, User } from 'firebase/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import routes from sub-apps
import { PortalRoutes } from '@aaas/app-portal';
import { AtsRoutes } from '@aaas/app-ats';
import { KmsRoutes } from '@aaas/app-kms';

// Create QueryClient instance
const queryClient = new QueryClient();

// Layout wrapper with Header
function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header onLogout={handleLogout} onNavigate={handleNavigate} />
      <main className="flex-1 pt-16">{children}</main>
    </div>
  );
}

// Conditional Layout - excludes widget embed page, login page, and public pages
function ConditionalLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isBetaBlocked } = useAuth();

  // Widget embed, login, welcome, join (invite acceptance), public pages,
  // and beta-blocked state render without the global Layout (no workspace
  // switcher / app dropdown — these pages are pre-workspace).
  if (
    isBetaBlocked ||
    location.pathname === '/kms/widget/embed' ||
    location.pathname === '/login' ||
    location.pathname === '/welcome' ||
    location.pathname === '/beta-access' ||
    location.pathname.startsWith('/auth/') ||
    location.pathname.startsWith('/join/') ||
    location.pathname.startsWith('/ats/public/')
  ) {
    return <>{children}</>;
  }

  return <Layout>{children}</Layout>;
}

function BetaBlockedRedirect() {
  const { isBetaBlocked } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isBetaBlocked && location.pathname !== '/beta-access') {
      navigate('/beta-access', { replace: true });
    }
  }, [isBetaBlocked, location.pathname, navigate]);

  return null;
}

function AppRoutes() {
  const { isBetaBlocked } = useAuth();

  if (isBetaBlocked) {
    return (
      <Routes>
        <Route path="*" element={<BetaAccessDeniedPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Beta access landing */}
      <Route path="/beta-access" element={<BetaAccessDeniedPage />} />

      {/* ATS public routes - bypass access guard */}
      <Route path="/ats/public/*" element={<AtsRoutes />} />

      {/* ATS routes - with access guard */}
      <Route
        path="/ats/*"
        element={
          <AppAccessGuard appSlug="ats">
            <AtsRoutes />
          </AppAccessGuard>
        }
      />

      {/* KMS widget embed - bypass access guard */}
      <Route path="/kms/widget/embed" element={<KmsRoutes />} />

      {/* KMS routes - with access guard */}
      <Route
        path="/kms/*"
        element={
          <AppAccessGuard appSlug="kms">
            <KmsRoutes />
          </AppAccessGuard>
        }
      />

      {/* Portal routes - no access guard needed (catch-all, must be last) */}
      <Route path="/*" element={<PortalRoutes />} />
    </Routes>
  );
}

// Initialize Firebase with config from environment variables
initializeFirebase({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
});

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load user profile from backend after Firebase auth
  const { userProfile, idToken, loading: profileLoading } = useBackendAuth(user);

  const loading = authLoading || profileLoading;

  // BrowserRouter must stay mounted at all times so the URL (including ?redirect=)
  // is never lost when auth loading state toggles. The loading spinner is rendered
  // INSIDE the router instead of replacing the entire tree.
  return (
    <QueryClientProvider client={queryClient}>
      <AppSubscriptionProvider>
        <AuthProvider user={user} userProfile={userProfile} idToken={idToken} loading={loading}>
          <BrowserRouter>
            {loading ? (
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              </div>
            ) : (
              <>
                <BetaBlockedRedirect />
                <ConditionalLayout>
                  <AppRoutes />
                </ConditionalLayout>
                <Toaster />
              </>
            )}
          </BrowserRouter>
        </AuthProvider>
      </AppSubscriptionProvider>
    </QueryClientProvider>
  );
}

export default App;
